import { useMemo, useState } from 'react';

import StakeholderPanel from './StakeholderPanel';

function Actors({
  actors,
  editarActor,
  eliminarActor,
  projectAlerts = [],
  projectSignals = [],
  projectTimeline = []
}) {
  const [selectedActor, setSelectedActor] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('todos');

  const normalizar = (texto) =>
    (texto || '').toString().toLowerCase().trim();

  const getAlertasActor = (actor) =>
    projectAlerts.filter((alerta) => alerta.actor === actor.nombre);

  const getSignalsActor = (actor) =>
    projectSignals.filter((signal) => signal.actor === actor.nombre);

  const getTimelineActor = (actor) =>
    projectTimeline.filter((evento) => evento.actor === actor.nombre);

  const filteredActors = useMemo(() => {
    return actors.filter((actor) => {
      const texto = [
        actor.nombre,
        actor.tipo,
        actor.estado,
        actor.riesgo,
        actor.comuna,
        actor.sector,
        actor.influencia,
        actor.observaciones,
        actor.tags
      ]
        .join(' ')
        .toLowerCase();

      const coincideBusqueda = texto.includes(normalizar(searchText));
      if (!coincideBusqueda) return false;

      const alertasActor = projectAlerts.filter((a) => a.actor === actor.nombre);
      const signalsActor = projectSignals.filter((s) => s.actor === actor.nombre);

      if (activeFilter === 'criticos') {
        return actor.estado === 'Crítico' || actor.riesgo === 'Crítico';
      }

      if (activeFilter === 'altaInfluencia') {
        return actor.influencia === 'Alta';
      }

      if (activeFilter === 'conAlertas') {
        return alertasActor.length > 0;
      }

      if (activeFilter === 'conRRSS') {
        return signalsActor.length > 0;
      }

      return true;
    });
  }, [actors, searchText, activeFilter, projectAlerts, projectSignals]);

  const totalCriticos = actors.filter(
    (actor) => actor.estado === 'Crítico' || actor.riesgo === 'Crítico'
  ).length;

  const totalAltaInfluencia = actors.filter(
    (actor) => actor.influencia === 'Alta'
  ).length;

  const totalConAlertas = actors.filter(
    (actor) => getAlertasActor(actor).length > 0
  ).length;

  const totalConRRSS = actors.filter(
    (actor) => getSignalsActor(actor).length > 0
  ).length;

  const filtros = [
    { key: 'todos', label: 'Todos', count: actors.length },
    { key: 'criticos', label: 'Críticos', count: totalCriticos },
    { key: 'altaInfluencia', label: 'Alta influencia', count: totalAltaInfluencia },
    { key: 'conAlertas', label: 'Con alertas', count: totalConAlertas },
    { key: 'conRRSS', label: 'Con RRSS', count: totalConRRSS }
  ];

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Stakeholders 360</h2>
          <p style={styles.subtitle}>
            Perfil territorial, riesgos, alertas, señales sociales e historial por actor.
          </p>
        </div>

        <div style={styles.resultBadge}>
          {filteredActors.length} / {actors.length}
        </div>
      </div>

      <div style={styles.searchBlock}>
        <input
          style={styles.searchInput}
          placeholder="Buscar por nombre, comuna, riesgo, influencia, tags o sector..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        {searchText && (
          <button
            style={styles.clearButton}
            onClick={() => setSearchText('')}
          >
            Limpiar
          </button>
        )}
      </div>

      <div style={styles.filterBar}>
        {filtros.map((filter) => {
          const isActive = activeFilter === filter.key;

          return (
            <button
              key={filter.key}
              style={{
                ...styles.filterButton,
                ...(isActive ? styles.filterButtonActive : {})
              }}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
              <span style={styles.filterCount}>{filter.count}</span>
            </button>
          );
        })}
      </div>

      {actors.length === 0 && (
        <p style={styles.empty}>No hay stakeholders registrados.</p>
      )}

      {actors.length > 0 && filteredActors.length === 0 && (
        <p style={styles.empty}>
          No hay stakeholders que coincidan con la búsqueda o filtro seleccionado.
        </p>
      )}

      <div style={styles.grid}>
        {filteredActors.map((actor) => {
          const alertas = getAlertasActor(actor);
          const signals = getSignalsActor(actor);
          const timeline = getTimelineActor(actor);

          return (
            <div
              key={actor.id}
              style={styles.actorCard}
              onClick={() => setSelectedActor(actor)}
            >
              <div style={styles.actorTop}>
                <div>
                  <h3 style={styles.actorName}>{actor.nombre}</h3>
                  <p style={styles.meta}>
                    {actor.tipo || 'Tipo no definido'} · {actor.comuna || 'Sin comuna'}
                  </p>
                </div>

                <span
                  style={{
                    ...styles.statusBadge,
                    background:
                      actor.estado === 'Crítico'
                        ? '#fee4e2'
                        : actor.estado === 'Positivo'
                        ? '#dcfae6'
                        : '#fef0c7',
                    color:
                      actor.estado === 'Crítico'
                        ? '#b42318'
                        : actor.estado === 'Positivo'
                        ? '#027a48'
                        : '#b54708'
                  }}
                >
                  {actor.estado || 'Sin estado'}
                </span>
              </div>

              <div style={styles.infoGrid}>

  <Info
    label="Estado territorial"
    value={actor.estadoTerritorial || '-'}
  />

  <Info
    label="Sensibilidad"
    value={actor.sensibilidadTerritorial || '-'}
  />

  <Info
    label="Activación"
    value={actor.activacionTerritorial || '-'}
  />

  <Info
    label="Convergencia"
    value={actor.convergenciaNarrativa || '-'}
  />

  <Info
    label="Influencia"
    value={actor.influencia || '-'}
  />

  <Info
    label="Movilización"
    value={actor.capacidadMovilizacion || '-'}
  />

</div>

              <div style={styles.narrativeBox}>

  <div style={styles.narrativeLabel}>
    Narrativa dominante
  </div>

  <div style={styles.narrativeValue}>
    {actor.narrativaDominante || 'Sin narrativa dominante definida'}
  </div>

  <p style={styles.executiveText}>
    {actor.interpretacionEjecutiva ||
      actor.observaciones ||
      'Sin interpretación territorial registrada.'}
  </p>

</div>

              <div style={styles.relationGrid}>
                <MiniMetric label="Alertas" value={alertas.length} />
                <MiniMetric label="RRSS" value={signals.length} />
                <MiniMetric label="Timeline" value={timeline.length} />
              </div>

              <div style={styles.actions}>
                <button
                  style={styles.viewButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedActor(actor);
                  }}
                >
                  Ver ficha 360
                </button>

                <button
                  style={styles.editButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    editarActor(actor);
                  }}
                >
                  Editar
                </button>

                <button
                  style={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    eliminarActor(actor.id);
                  }}
                >
                  Eliminar
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <StakeholderPanel
        actor={selectedActor}
        onClose={() => setSelectedActor(null)}
        onEdit={(actor) => {
          setSelectedActor(null);
          editarActor(actor);
        }}
        alertas={selectedActor ? getAlertasActor(selectedActor) : []}
        signals={selectedActor ? getSignalsActor(selectedActor) : []}
        timeline={selectedActor ? getTimelineActor(selectedActor) : []}
      />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div style={styles.infoItem}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div style={styles.miniMetric}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

const styles = {
  card: {
    background: 'white',
    padding: '30px',
    borderRadius: '20px',
    boxShadow: '0 12px 35px rgba(7,26,47,0.08)',
    marginTop: '30px'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: '22px'
  },

  title: {
    margin: 0,
    color: '#071a2f',
    fontSize: '28px'
  },

  subtitle: {
    color: '#667085',
    marginTop: '8px'
  },

  resultBadge: {
    background: '#eef4ff',
    color: '#092395',
    padding: '10px 14px',
    borderRadius: '999px',
    fontWeight: 'bold',
    fontSize: '13px',
    whiteSpace: 'nowrap'
  },

  searchBlock: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px'
  },

  searchInput: {
    flex: 1,
    padding: '15px',
    borderRadius: '14px',
    border: '1px solid #d0d5dd',
    fontSize: '15px'
  },

  clearButton: {
    background: '#071a2f',
    color: 'white',
    border: 'none',
    padding: '0 18px',
    borderRadius: '14px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '24px'
  },

  filterButton: {
    background: '#f8fafc',
    color: '#344054',
    border: '1px solid #e4e7ec',
    padding: '10px 14px',
    borderRadius: '999px',
    cursor: 'pointer',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  filterButtonActive: {
    background: 'linear-gradient(135deg,#1fa97a,#0993b5)',
    color: 'white',
    border: '1px solid transparent'
  },

  filterCount: {
    background: 'rgba(255,255,255,0.25)',
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '12px'
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))',
    gap: '20px'
  },

  actorCard: {
    border: '1px solid #e4e7ec',
    borderRadius: '20px',
    padding: '22px',
    background: '#ffffff',
    boxShadow: '0 8px 24px rgba(7,26,47,0.05)',
    cursor: 'pointer',
    transition: '0.2s'
  },

  actorTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '14px',
    marginBottom: '18px'
  },

  actorName: {
    margin: 0,
    color: '#071a2f',
    fontSize: '22px'
  },

  meta: {
    color: '#667085',
    margin: '6px 0 0'
  },

  statusBadge: {
    padding: '6px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 'bold',
    height: 'fit-content'
  },

  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2,1fr)',
    gap: '10px',
    marginBottom: '16px'
  },

  infoItem: {
    background: '#f8fafc',
    padding: '12px',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '13px',
    color: '#667085'
  },

  observaciones: {
    color: '#475467',
    lineHeight: '1.5',
    minHeight: '44px'
  },

  relationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,1fr)',
    gap: '10px',
    margin: '18px 0'
  },

  miniMetric: {
    background: '#eef4ff',
    color: '#092395',
    padding: '12px',
    borderRadius: '14px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },

  actions: {
    display: 'flex',
    gap: '10px'
  },

  viewButton: {
    background: '#071a2f',
    color: 'white',
    border: 'none',
    padding: '11px 14px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  editButton: {
    background: '#1fa97a',
    color: 'white',
    border: 'none',
    padding: '11px 14px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  deleteButton: {
    background: '#dc2626',
    color: 'white',
    border: 'none',
    padding: '11px 14px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },

  empty: {
    color: '#667085'
  },
  narrativeBox: {
  background: '#f8fafc',
  border: '1px solid #e4e7ec',
  borderRadius: '14px',
  padding: '14px',
  marginBottom: '16px'
},

narrativeLabel: {
  color: '#0993b5',
  fontSize: '11px',
  fontWeight: '900',
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  marginBottom: '6px'
},

narrativeValue: {
  color: '#071a2f',
  fontSize: '15px',
  fontWeight: '900',
  marginBottom: '8px'
},

executiveText: {
  color: '#475467',
  fontSize: '13px',
  lineHeight: '1.5',
  margin: 0
},
};

export default Actors;